import { ErrorModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	Grid,
	makeStyles,
	MenuItem,
	MenuList,
	Paper,
	Snackbar,
	Typography,
} from "@material-ui/core";
import { type SyntheticEvent, useEffect, useState } from "react";
import { TemplateDrawingsTable } from "./resources-tables/TemplateDrawings/TemplateDrawingsTable";
import { Alert } from "@material-ui/lab";
import { AssetDrawingsPicker } from "./resources-tables/TemplateDrawings/components/asset-drawings/asset-drawings-picker";
import { useSearchTemplateDrawings } from "./resources-tables/TemplateDrawings/hooks/useSearchTemplateDrawings";
import { TemplateLOTOTable } from "./resources-tables/TemplateLOTO/TemplateLOTOTable";
import { AssetLOTOPicker } from "./resources-tables/TemplateLOTO/components/asset-LOTO/asset-LOTO-picker";
import { useSearchTemplateLOTO } from "./resources-tables/TemplateLOTO/hooks/useSearchTemplateLOTO";
import { TemplateAttachmentsTable } from "./resources-tables/TemplateAttachments/TemplateAttachmentsTable";
import { useSearchTemplateAttachments } from "./resources-tables/TemplateAttachments/hooks/useSearchTemplateAttachments";
import { AssetAttachmentPicker } from "./resources-tables/TemplateAttachments/components/asset-Attachments/asset-Attachments-picker";
import { TemplatePartsTable } from "./resources-tables/TemplateParts/TemplatePartsTable";
import { useSearchTemplateParts } from "./resources-tables/TemplateParts/hooks/useSearchTemplateParts";
import { AssetPartsPicker } from "./resources-tables/TemplateParts/components/asset-parts/asset-parts-picker";
import { ImportAssetResourcesDialog } from "./resources-tables/asset-resources-dialogs/import-asset-resources-dialog";
import { AlertCreatingLOTODialog } from "./resources-tables/TemplateLOTO/components/alert-creating-LOTO-dialog";
import { getCrafts } from "@/pages/task-list/repositorie/TaskListRepositorie";
import { get } from "lodash-es";
import { useTemplatesContext } from "../../context/templatesContext";
import { useSearchAssetResources } from "../hooks/useSearchAssetResources";
import { useSearchTemplateResources } from "../hooks/useSearchTemplateResources";
import type { Craft } from "@/shared/work-orders-modals/models/Craft";

const useStyles = makeStyles((theme) => ({
	root: {
		height: "100%",
	},
	resourcesList: {
		height: "100%",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
	},
	menuList: {
		flexGrow: 1,
		overflowY: "auto",
		padding: 0,
	},
	menuItem: {
		borderBottom: `1px solid ${theme.palette.divider}`,
		minWidth: "250px",
	},
	menuItemSelected: {
		backgroundColor: "rgba(173, 216, 230, 0.2)",
	},
	menuItemContent: {
		width: "100%",
		padding: theme.spacing(2),
	},
	tableContainer: {
		height: "100%",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	drawingsMenuItem: {
		backgroundColor: "rgba(147, 112, 219, 0.1)",
	},
	attachmentsMenuItem: {
		backgroundColor: "rgba(255, 165, 0, 0.1)",
	},
	lotoMenuItem: {
		backgroundColor: "rgba(50, 205, 50, 0.1)",
	},
	partsMenuItem: {
		backgroundColor: "rgba(70, 130, 180, 0.1)",
	},
}));

export type ResourceType = "DRAWINGS" | "ATTACHMENTS" | "LOTO" | "PARTS";

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	AssetId: number | null;
	TemplateId: number | null;
	newMode?: boolean;
	initialResource?: ResourceType;
	Title: string;
	AssetName: string;
	TypeCode: string;
	CraftId?: number;
};

export const TableTemplateResoures = ({
	show,
	onHide,
	AssetId,
	TemplateId,
	newMode,
	initialResource,
	Title,
	AssetName,
	TypeCode,
	CraftId,
}: Props) => {
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("xl");
	const classes = useStyles();

	const {
		state: {
			assetDrawings,
			assetAttachments,
			assetParts,
			assetLOTO,
			templateAttachmentsList,
			templateDrawingsList,
			templateLOTOList,
			templatePartsList,
			isCreatingLOTO,
		},
		actions: {
			setAssetAttachmentsList,
			setAssetDrawings,
			setAssetLOTOList,
			setAssetPartsList,
			setTemplateAttachmentsList,
			setTemplateDrawingsList,
			setTemplateLOTOList,
			setTemplatePartsList,
			setIsCreatingLOTO,
		},
	} = useTemplatesContext();

	const [isTemplateDrawingsLoading, setIsTemplateDrawingsLoading] =
		useState<boolean>(false);
	const [templateDrawingsError, setTemplateDrawingsError] =
		useState<string>("");

	const [isTemplateLOTOLoading, setIsTemplateLOTOLoading] =
		useState<boolean>(false);
	const [templateLOTOError, setTemplateLOTOError] = useState<string>("");

	const [isTemplateAttachmentsLoading, setIsTemplateAttachmentsLoading] =
		useState<boolean>(false);

	const [templatePartsLoading, setIsTemplatePartsLoading] =
		useState<boolean>(false);

	const [templatePartsError, setTemplatePartsError] = useState<string>("");

	const [templateAttachmentsError, setTemplateAttachmentsError] =
		useState<string>("");

	const [isLoadingResourcesList, setIsLoadingResourcesList] =
		useState<boolean>(false);
	const [errorResourcesList, setErrorResourcesList] = useState<string>("");
	const [assetResourcesMessage, setAssetResourcesMessage] = useState(false);

	const [selectedItem, setSelectedItem] = useState<string | null>(
		initialResource ? initialResource : "DRAWINGS",
	);

	const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

	const [showTemplateDrawingsTable, setShowTemplateDrawingsTable] =
		useState(false);
	const [showTemplateAttachmentsTable, setShowTemplateAttachmentsTable] =
		useState(false);
	const [showTemplateLOTOTable, setShowTemplateLOTOTable] = useState(false);
	const [showTemplatePartsTable, setShowTemplatePartsTable] = useState(false);

	const [showAssetDrawingsPicker, setShowAssetDrawingsPicker] = useState(false);
	const [showAssetAttachmentsPicker, setShowAssetAttachmentsPicker] =
		useState(false);
	const [showAssetLOTOPicker, setShowAssetLOTOPicker] = useState(false);
	const [showAssetPartsPicker, setShowAssetPartsPicker] = useState(false);

	const [isLoadingTemplateResourcesList, setIsLoadingTemplateResourcesList] =
		useState<boolean>(false);

	const [errorTemplateResourcesList, setErrorTemplateResourcesList] =
		useState<string>("");

	const [showImportDialog, setShowImportDialog] = useState(false);
	const [craftName, setCraftName] = useState<string>("");

	const searchTemplateDrawings = useSearchTemplateDrawings({
		TemplateId,
		setIsTemplateDrawingsLoading,
		setTemplateDrawingsError,
	});

	const searchTemplateLOTO = useSearchTemplateLOTO({
		TemplateId,
		setIsTemplateLOTOLoading,
		setTemplateLOTOError,
	});

	const searchTemplateAttachments = useSearchTemplateAttachments({
		TemplateId,
		setIsTemplateAttachmentsLoading,
		setTemplateAttachmentsError,
	});

	const searchTemplateParts = useSearchTemplateParts({
		TemplateId,
		setIsTemplatePartsLoading,
		setTemplatePartsError,
	});

	const searchAssetResources = useSearchAssetResources({
		AssetId,
		setAssetAttachmentsList,
		setAssetDrawings,
		setAssetLOTOList,
		setAssetPartsList,
		setErrorResourcesList,
		setIsLoadingResourcesList,
		setAssetResourcesMessage,
	});

	const searchTemplateResources = useSearchTemplateResources({
		TemplateId,
		setTemplateAttachmentsList,
		setTemplateDrawingsList,
		setTemplateLOTOList,
		setTemplatePartsList,
		setErrorTemplateResourcesList,
		setIsLoadingTemplateResourcesList,
	});

	const handleCloseResourcesMessage = (
		event?: SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setAssetResourcesMessage(false);
	};

	const handleCancel = () => {
		onHide(false);
		setTemplateDrawingsList([]);
		setTemplateLOTOList([]);
		setTemplateAttachmentsList([]);
		setTemplatePartsList([]);
	};

	const handleItemClick = (item: string) => {
		setSelectedItem(item);
		setShowTemplateAttachmentsTable(item === "ATTACHMENTS");
		setShowTemplateDrawingsTable(item === "DRAWINGS");
		setShowTemplateLOTOTable(item === "LOTO");
		setShowTemplatePartsTable(item === "PARTS");
	};

	const showPicker = (item: string) => {
		switch (item) {
			case "DRAWINGS":
				setShowAssetDrawingsPicker(true);
				break;
			case "ATTACHMENTS":
				setShowAssetAttachmentsPicker(true);
				break;
			case "LOTO":
				if (isCreatingLOTO) {
					setShowConfirmDialog(true);
				} else {
					setShowAssetLOTOPicker(true);
				}
				break;
			case "PARTS":
				setShowAssetPartsPicker(true);
				break;
			default:
				console.error("Unknown item type");
		}
	};

	const isButtonDisabled = () => {
		switch (selectedItem) {
			case "DRAWINGS":
				return assetDrawings.length === 0;
			case "ATTACHMENTS":
				return assetAttachments.length === 0;
			case "LOTO":
				return assetLOTO.length === 0;
			case "PARTS":
				return assetParts.length === 0;
			default:
				return true;
		}
	};

	useEffect(() => {
		if (show) {
			searchAssetResources();
			searchTemplateResources();
			setSelectedItem(initialResource ? initialResource : "DRAWINGS");
			setShowTemplateDrawingsTable(initialResource === "DRAWINGS");
			setShowTemplateAttachmentsTable(initialResource === "ATTACHMENTS");
			setShowTemplateLOTOTable(initialResource === "LOTO");
			setShowTemplatePartsTable(initialResource === "PARTS");
			if (!initialResource) setShowTemplateDrawingsTable(true);
		} else {
			setSelectedItem("DRAWINGS");
			setShowTemplateDrawingsTable(false);
			setShowTemplateAttachmentsTable(false);
			setShowTemplateLOTOTable(false);
			setShowTemplatePartsTable(false);
			handleCancel();
		}
	}, [show, initialResource]);

	const getCraftName = async (craftId: number) => {
		try {
			const res = await getCrafts();
			if (res.ok) {
				const craftsData = get(res, "data.tables[0].rows", []);
				const craft = craftsData.find((c: Craft) => c.CraftId === craftId);
				if (craft) {
					setCraftName(craft.CraftName);
				}
			}
		} catch (error) {
			console.error("Error fetching craft name:", error);
		}
	};

	useEffect(() => {
		if (CraftId) {
			getCraftName(CraftId);
		}
	}, [CraftId]);

	useEffect(() => {
		if (show && newMode) {
			setShowImportDialog(true);
		}
	}, [show, newMode]);

	return (
		<>
			<Dialog
				maxWidth={maxWidth}
				open={show}
				onClose={handleCancel}
				fullWidth={true}
				style={{ margin: "0", padding: 5, width: "100% !important" }}
				className={classes.root}
			>
				<Grid
					container
					justifyContent="center"
					style={{
						height: "100vh",
						paddingBottom: 0,
						display: "flex",
						alignItems: "center",
					}}
				>
					<Grid
						item
						md={12}
						xs={12}
						style={{
							padding: "0 30px",
							height: "auto",
							minHeight: "8vh",
						}}
					>
						<Typography
							variant="h5"
							style={{
								fontWeight: 600,
								paddingTop: 10,
							}}
						>
							TEMPLATE RESOURCES
						</Typography>

						<Typography
							style={{
								display: "flex",
								alignItems: "center",
								fontSize: "1.3rem",
								flexWrap: "wrap",
								gap: "8px",
								padding: "8px 0 0",
							}}
						>
							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Template:</span>
								<span style={{ marginLeft: "4px" }}>{Title}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}
							>
								|
							</div>
							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Asset:</span>
								<span style={{ marginLeft: "4px" }}>{AssetName}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}
							>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Type:</span>
								<span style={{ marginLeft: "4px" }}>{TypeCode}</span>
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 8px",
								}}
							>
								|
							</div>

							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									margin: "4px 0",
								}}
							>
								<span style={{ fontWeight: 600 }}>Craft:</span>
								<span style={{ marginLeft: "4px" }}>{craftName}</span>
							</div>
						</Typography>
					</Grid>
					<DialogContent
						dividers
						style={{ height: "66vh", paddingTop: "20px", overflowY: "hidden" }}
					>
						<Grid
							container
							justifyContent="space-between"
							alignItems="stretch"
							className={classes.root}
							style={{ height: "100%" }}
						>
							<Grid item md={3} xs={12} style={{ height: "100%" }}>
								<Paper elevation={1} className={classes.resourcesList}>
									<MenuList className={`mes-submenu ${classes.menuList}`}>
										<MenuItem
											className={`${classes.menuItem} ${
												classes.drawingsMenuItem
											} ${
												selectedItem === "DRAWINGS"
													? classes.menuItemSelected
													: ""
											}`}
											onClick={() => handleItemClick("DRAWINGS")}
										>
											<div className={classes.menuItemContent}>
												<Typography variant="h6">
													DRAWINGS
													{isLoadingResourcesList ? (
														<CircularProgress
															size={20}
															style={{
																marginLeft: "8px",
																verticalAlign: "middle",
															}}
														/>
													) : (
														templateDrawingsList.length > 0 &&
														` (${templateDrawingsList.length})`
													)}
												</Typography>
											</div>
										</MenuItem>
										<MenuItem
											className={`${classes.menuItem} ${
												classes.attachmentsMenuItem
											} ${
												selectedItem === "ATTACHMENTS"
													? classes.menuItemSelected
													: ""
											}`}
											onClick={() => handleItemClick("ATTACHMENTS")}
										>
											<div className={classes.menuItemContent}>
												<Typography variant="h6">
													ATTACHMENTS
													{isLoadingResourcesList ? (
														<CircularProgress
															size={20}
															style={{
																marginLeft: "8px",
																verticalAlign: "middle",
															}}
														/>
													) : (
														templateAttachmentsList.length > 0 &&
														` (${templateAttachmentsList.length})`
													)}
												</Typography>
											</div>
										</MenuItem>
										<MenuItem
											className={`${classes.menuItem} ${classes.lotoMenuItem} ${
												selectedItem === "LOTO" ? classes.menuItemSelected : ""
											}`}
											onClick={() => handleItemClick("LOTO")}
										>
											<div className={classes.menuItemContent}>
												<Typography variant="h6">
													LOTO
													{isLoadingResourcesList ? (
														<CircularProgress
															size={20}
															style={{
																marginLeft: "8px",
																verticalAlign: "middle",
															}}
														/>
													) : (
														templateLOTOList.length > 0 &&
														` (${templateLOTOList.length})`
													)}
												</Typography>
											</div>
										</MenuItem>
										<MenuItem
											className={`${classes.menuItem} ${
												classes.partsMenuItem
											} ${
												selectedItem === "PARTS" ? classes.menuItemSelected : ""
											}`}
											onClick={() => handleItemClick("PARTS")}
										>
											<div className={classes.menuItemContent}>
												<Typography variant="h6">
													PARTS
													{isLoadingResourcesList ? (
														<CircularProgress
															size={20}
															style={{
																marginLeft: "8px",
																verticalAlign: "middle",
															}}
														/>
													) : (
														templatePartsList.length > 0 &&
														` (${templatePartsList.length})`
													)}
												</Typography>
											</div>
										</MenuItem>
									</MenuList>
								</Paper>
							</Grid>
							<Grid item md={9} xs={12} style={{ height: "100%" }}>
								<Paper elevation={1} className={classes.tableContainer}>
									<TemplateDrawingsTable
										AssetId={AssetId}
										newMode={newMode}
										TemplateId={TemplateId}
										searchAssetResources={searchAssetResources}
										show={showTemplateDrawingsTable}
										onHide={() => {
											setShowTemplateDrawingsTable(false);
										}}
										setShowAssetDrawingsPicker={setShowAssetDrawingsPicker}
										isLoadingResourcesList={isLoadingResourcesList}
										TemplateName={Title}
										AssetName={AssetName}
										Title={Title}
									/>
									<TemplateLOTOTable
										AssetId={AssetId}
										newMode={newMode}
										TemplateId={TemplateId}
										searchAssetResources={searchAssetResources}
										show={showTemplateLOTOTable}
										onHide={() => {
											setShowTemplateLOTOTable(false);
										}}
										setShowAssetLOTOPicker={setShowAssetLOTOPicker}
										isLoadingResourcesList={isLoadingResourcesList}
										AssetName={AssetName as string}
										TemplateName={Title as string}
									/>
									<TemplateAttachmentsTable
										AssetId={AssetId}
										newMode={newMode}
										TemplateId={TemplateId}
										searchAssetResources={searchAssetResources}
										show={showTemplateAttachmentsTable}
										onHide={() => {
											setShowTemplateAttachmentsTable(false);
										}}
										setShowAssetAttachmentsPicker={
											setShowAssetAttachmentsPicker
										}
										isLoadingResourcesList={isLoadingResourcesList}
										TemplateName={Title}
										AssetName={AssetName}
									/>
									<TemplatePartsTable
										AssetId={AssetId}
										newMode={newMode}
										TemplateId={TemplateId}
										searchAssetResources={searchAssetResources}
										show={showTemplatePartsTable}
										onHide={() => {
											setShowTemplatePartsTable(false);
										}}
										AssetName={AssetName as string}
										TemplateName={Title as string}
										setShowAssetPartsPicker={setShowAssetPartsPicker}
										isLoadingResourcesList={isLoadingResourcesList}
									/>
								</Paper>
							</Grid>
						</Grid>
					</DialogContent>

					<Grid item md={12} xs={12} style={{ height: "50px" }}>
						<DialogActions
							style={{
								padding: "0px 17px",
								height: "100%",
								display: "flex",
								alignItems: "center",
							}}
						>
							<Grid
								container
								spacing={2}
								style={{ margin: 0, display: "flex", alignItems: "center" }}
							>
								<Grid item md={3} xs={4}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={handleCancel}
									>
										Close
									</Button>
								</Grid>
							</Grid>
							{selectedItem && (
								<Grid item md={4} xs={12} style={{ paddingRight: "8px" }}>
									<Button
										variant="contained"
										color="primary"
										onClick={() => showPicker(selectedItem)}
										fullWidth
										disabled={isButtonDisabled()}
										style={{ minWidth: 300, height: 40 }}
									>
										{`${selectedItem}`} from asset
									</Button>
								</Grid>
							)}
						</DialogActions>
						<AssetDrawingsPicker
							show={showAssetDrawingsPicker}
							TemplateId={TemplateId}
							onHide={(shouldUpdate) => {
								if (shouldUpdate) {
									searchTemplateDrawings();
								}
								setShowAssetDrawingsPicker(false);
							}}
							TemplateName={Title}
							AssetName={AssetName}
							Title={Title}
						/>
						<AssetLOTOPicker
							show={showAssetLOTOPicker}
							TemplateId={TemplateId}
							onHide={(shouldUpdate) => {
								if (shouldUpdate) {
									searchTemplateLOTO();
								}
								setShowAssetLOTOPicker(false);
							}}
							TemplateName={Title}
							AssetName={AssetName}
							Title={Title}
						/>
						<AssetAttachmentPicker
							show={showAssetAttachmentsPicker}
							TemplateId={TemplateId}
							onHide={(shouldUpdate) => {
								if (shouldUpdate) {
									searchTemplateAttachments();
								}
								setShowAssetAttachmentsPicker(false);
							}}
							TemplateName={Title}
							AssetName={AssetName}
							Title={Title}
						/>
						<AssetPartsPicker
							show={showAssetPartsPicker}
							TemplateId={TemplateId}
							onHide={(shouldUpdate) => {
								if (shouldUpdate) {
									searchTemplateParts();
								}
								setShowAssetPartsPicker(false);
							}}
							TemplateName={Title}
							AssetName={AssetName}
							Title={Title}
						/>
						<AlertCreatingLOTODialog
							showConfirmDialog={showConfirmDialog}
							setShowConfirmDialog={setShowConfirmDialog}
							setIsCreatingLOTO={setIsCreatingLOTO}
							openPicker={setShowAssetLOTOPicker}
							TemplateId={TemplateId}
						/>
					</Grid>
					<ImportAssetResourcesDialog
						open={showImportDialog}
						onClose={() => setShowImportDialog(false)}
						onOpenAssetPartsPicker={() => {
							setShowAssetPartsPicker(true);
						}}
						onOpenAssetLOTOPicker={() => {
							setShowAssetLOTOPicker(true);
						}}
						onOpenAssetAttachmentsPicker={() => {
							setShowAssetAttachmentsPicker(true);
						}}
						onOpenAssetDrawingsPicker={() => {
							setShowAssetDrawingsPicker(true);
						}}
					/>
					<ErrorModal
						error={errorResourcesList}
						onHide={() => setErrorResourcesList("")}
					/>
					<ErrorModal
						error={templateDrawingsError}
						onHide={() => setTemplateDrawingsError("")}
					/>
					<ErrorModal
						error={templateLOTOError}
						onHide={() => setTemplateLOTOError("")}
					/>
					<ErrorModal
						error={templateAttachmentsError}
						onHide={() => setTemplateAttachmentsError("")}
					/>
					<ErrorModal
						error={templatePartsError}
						onHide={() => setTemplatePartsError("")}
					/>
					<Snackbar
						open={assetResourcesMessage}
						autoHideDuration={2500}
						onClose={handleCloseResourcesMessage}
					>
						<Alert severity="warning" onClose={handleCloseResourcesMessage}>
							The asset has no assigned resources
						</Alert>
					</Snackbar>
				</Grid>
			</Dialog>
		</>
	);
};
